"use server"

import { sdk } from "@lib/config"
import { revalidateTag } from "next/cache"
import { getAuthHeaders, getCacheTag, getCartId } from "@lib/data/cookies"

/**
 * Atomically changes a cart line item's variant (size) via delete + re-add.
 *
 * Medusa's `updateLineItem` does not support changing `variant_id`, so we
 * must delete the old line item and create a new one. This server action
 * performs both operations in a single round-trip and revalidates the cache
 * only once at the end, avoiding the intermediate re-render where the item
 * would briefly disappear.
 *
 * Metadata is preserved so Medusa doesn't merge the re-added item with
 * another line item that has the same variant_id (Medusa merges when both
 * variant_id and metadata match).
 */
export async function changeSizeAction({
  lineId,
  originalVariantId,
  newVariantId,
  quantity,
  metadata,
}: {
  lineId: string
  originalVariantId: string
  newVariantId: string
  quantity: number
  metadata?: Record<string, unknown> | null
}): Promise<{ success: boolean; error?: string }> {
  if (!newVariantId) {
    return { success: false, error: "Missing new variant ID" }
  }

  const cartId = await getCartId()
  if (!cartId) {
    return { success: false, error: "No existing cart found" }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const lineItemPayload = (variantId: string) => ({
    variant_id: variantId,
    quantity,
    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
  })

  // Step 1: Delete the old line item (no cache revalidation yet)
  await sdk.store.cart.deleteLineItem(cartId, lineId, {}, headers)

  // Step 2: Add the new variant
  try {
    await sdk.store.cart.createLineItem(
      cartId,
      lineItemPayload(newVariantId),
      {},
      headers
    )
  } catch (addErr) {
    // Add failed -- attempt to restore the original variant
    try {
      await sdk.store.cart.createLineItem(
        cartId,
        lineItemPayload(originalVariantId),
        {},
        headers
      )
    } catch {
      // Restore also failed -- revalidate so UI reflects the deletion
      const cartCacheTag = await getCacheTag("carts")
      revalidateTag(cartCacheTag)
      return {
        success: false,
        error: "Failed to change size. Your original item could not be restored -- please re-add it manually.",
      }
    }

    // Restore succeeded -- revalidate and report the add failure
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    const message = addErr instanceof Error ? addErr.message : "Failed to change size"
    return { success: false, error: message }
  }

  // Success -- revalidate once
  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  const fulfillmentCacheTag = await getCacheTag("fulfillment")
  revalidateTag(fulfillmentCacheTag)

  return { success: true }
}
